import React from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

interface CopyOnceKeyModalProps {
  open: boolean
  plaintext: string | null
  label: string
  onClose: () => void
  snippets?: {
    curl?: string
    headers?: Record<string, string>
  }
}

const CopyOnceKeyModal: React.FC<CopyOnceKeyModalProps> = ({ open, plaintext, label, onClose, snippets }) => {
  const [showSecret, setShowSecret] = React.useState(false)
  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'error'>('idle')
  const [copyError, setCopyError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setShowSecret(false)
      setCopyStatus('idle')
      setCopyError(null)
    }
  }, [open])

  const handleCopy = async () => {
    if (!plaintext) {
      return
    }

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(plaintext)
      } else {
        const textarea = document.createElement('textarea')
        textarea.value = plaintext
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      }
      setCopyStatus('copied')
      setCopyError(null)
    } catch (error) {
      console.warn('Failed to copy ingest key', error)
      setCopyStatus('error')
      setCopyError('Copy failed. Use Ctrl+C or Command+C to copy manually.')
    }
  }

  const renderSnippet = () => {
    if (!snippets?.curl) {
      return null
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Example cURL request
        </Typography>
        <Box
          component="pre"
          sx={{
            bgcolor: 'background.default',
            borderRadius: 2,
            p: 2,
            fontFamily: 'monospace',
            overflowX: 'auto',
            fontSize: '0.85rem',
          }}
        >
          {snippets.curl}
        </Box>
      </Box>
    )
  }

  const renderHeaders = () => {
    const headers = snippets?.headers

    if (!headers || Object.keys(headers).length === 0) {
      return null
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Required headers (e.g. Pipedream)
        </Typography>
        <List dense disablePadding>
          {Object.entries(headers).map(([name, value]) => (
            <ListItem key={name} disableGutters>
              <ListItemText primary={name} secondary={value} />
            </ListItem>
          ))}
        </List>
      </Box>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy your new ingest key</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Stack spacing={1}>
            <Typography variant="subtitle1">{label}</Typography>
            <Typography color="text.secondary">
              Shown only once. Store securely; you cannot retrieve it later.
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <OutlinedInput
              value={plaintext ?? ''}
              type={showSecret ? 'text' : 'password'}
              readOnly
              sx={{ flexGrow: 1 }}
              endAdornment={
                <InputAdornment position="end">
                  <Tooltip title={showSecret ? 'Hide key' : 'Show key'}>
                    <IconButton onClick={() => setShowSecret((prev) => !prev)} edge="end">
                      {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              }
            />
            <Button
              variant="contained"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              disabled={!plaintext}
            >
              Copy
            </Button>
          </Stack>
          {copyStatus === 'copied' ? (
            <Alert severity="success">Ingest key copied to clipboard.</Alert>
          ) : null}
          {copyError ? <Alert severity="warning">{copyError}</Alert> : null}
          {renderSnippet()}
          {renderHeaders()}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CopyOnceKeyModal
